import { ApiClient } from './api-client';
import { Observable, throwError } from 'rxjs';
import { Endpoints } from './endpoints';
import { inject, Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { CustomError } from '../models/shared/custom-error';
import { BaseType } from '../models/base/base-type';
import { State } from '../models/types/dto/state';
import { Gender } from '../models/types/dto/gender';
import { Country } from '../models/types/dto/country';
import { SubmissionStatus } from '../models/shared/submission-status';
import { AlertType } from '../models/alerts/dto/alert-types';
import { TransactionEntryCodeType } from '../models/transactions/transaction-entry-code-type';
import { TransactionStatus } from '../models/transactions/transaction-status';
import { Pronoun } from '../models/users/dto/pronoun';
import { MfaType } from '../models/types/dto/mfa-type';
import { EmploymentStatusType } from '../models/types/dto/employment-status-type';
import { StatusReasonType } from '../models/types/dto/status-reason-type';
import { RemittanceStatusType } from '../models/remittances/remittance-status-type';
import { PaymentMethodType } from '../models/types/dto/payment-method-type';
import { RemittanceFrequency } from '../models/remittances/remittance-frequency';
import { ScheduledRemittanceStatus } from '../models/remittances/scheduled-remittance-status';
import { FundType } from '../models/transactions/fund-type';
import { AccountType } from '../models/transactions/account-type';
import { RelationshipStatusType } from '../models/types/dto/relationship-status-type';
import { BeneficiaryRelationshipToMemberType } from '../views/beneficiaries/models/beneficiary-relationship-to-member-type';
import { FiduciaryType } from '../views/fiduciaries/models/fiduciary-type';
import { FiduciaryEntityType } from '../views/fiduciaries/models/fiduciary-entity-type';
import { FiduciaryPowerOfAttorneyType } from '../views/fiduciaries/models/fiduciary-power-of-attorney-type';
import { FiduciaryPowerOfAttorneyAppointmentType } from '../views/fiduciaries/models/fiduciary-power-of-attorney-appointment-type';
import { AccountStatusType } from '../models/transactions/account-status-type';
import { EmployerStatusType } from '../views/records/components/employers/ip-employers-table/models/employer-status-type';
import { MemberStatusType } from '../models/users/dto/member-status-type';
import { ConsentWaiverType } from '../models/consent-waivers/consent-waiver-type';
import { TaxStatusType } from '../models/taxes/tax-status-type';
import { TaxFormType } from '../models/taxes/tax-form-type';
import { TaxResidencyType } from '../models/taxes/tax-residency-type';
import { TaxProfileSourceType } from '../models/taxes/tax-profile-source-type';
import { PrescribedTaxZoneType } from '../models/taxes/prescribed-tax-zone-type';
import { TaxWithholdingOverrideType } from '../models/taxes/tax-withholding-override-type';
import { TaxCraLetterOfAuthorityType } from '../models/taxes/tax-cra-letter-of-authority-type';
import { RetirementType } from '../models/retirement-stub/retirement-type';
import { LifeStatusType } from '../models/life-status/life-status-type';
import { FinancialInstitutionType } from '../models/types/dto/financial-institution-type';
import { MemberContributionFrequencyType } from '../models/types/dto/member-contribution-frequency-type';
import { CreditCardStatusType } from '../models/types/dto/credit-card-status-type';

@Injectable({
  providedIn: 'root'
})
export class TypeAPI implements LoggableAPI {
  private apiClient = inject(ApiClient);

  // Variables

  public serviceName = 'Types';

  // States

  public getStatesByCountry(countryId: number): Observable<State[]> {
    const url = Endpoints.getStatesByCountryId(countryId);
    return this.apiClient.getArr<State>(State, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Genders

  public getGenders(): Observable<Gender[]> {
    const url = Endpoints.getGenders();
    return this.apiClient.getArr<Gender>(Gender, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Pronouns

  public getPronouns(): Observable<Pronoun[]> {
    const url = Endpoints.getPronouns();
    return this.apiClient.getArr<Pronoun>(Pronoun, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberRelationshipTypes(): Observable<RelationshipStatusType[]> {
    const url = Endpoints.getMemberRelationshipTypes();
    return this.apiClient.getArr<RelationshipStatusType>(RelationshipStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Countries

  public getCountries(): Observable<Country[]> {
    const url = Endpoints.getCountries();
    return this.apiClient.getArr<Country>(Country, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getCountriesWithStates(): Observable<Country[]> {
    const url = Endpoints.getCountriesWithStates();
    return this.apiClient.getArr<Country>(Country, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Submissions

  public getSubmissionStatusTypes(): Observable<SubmissionStatus[]> {
    const url = Endpoints.getSubmissionStatusTypes();
    return this.apiClient.getArr<SubmissionStatus>(SubmissionStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Transactions

  public getTransactionStatusTypes(): Observable<TransactionStatus[]> {
    const url = Endpoints.getTransactionStatusTypes();
    return this.apiClient.getArr<TransactionStatus>(TransactionStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Remittances

  public getRemittanceStatusTypes(): Observable<RemittanceStatusType[]> {
    const url = Endpoints.getRemittanceStatusTypes();
    return this.apiClient.getArr<RemittanceStatusType>(RemittanceStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRemittancePaymentStatusTypes(): Observable<RemittanceStatusType[]> {
    const url = Endpoints.getRemittancePaymentStatusTypes();
    return this.apiClient.getArr<RemittanceStatusType>(RemittanceStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Contribution Entry Options

  public getContributionEntryCodeTypes(): Observable<TransactionEntryCodeType[]> {
    const url = Endpoints.getContributionEntryOptionTypes();
    return this.apiClient.getArr<TransactionEntryCodeType>(TransactionEntryCodeType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Entries

  public getTransactionEntryCodeTypes(): Observable<TransactionEntryCodeType[]> {
    const url = Endpoints.getTransactionEntryCodeTypes();
    return this.apiClient.getArr<TransactionEntryCodeType>(TransactionEntryCodeType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Alerts

  public getAlertTypes(): Observable<AlertType[]> {
    const url = Endpoints.getAlertTypes();
    return this.apiClient.getArr<AlertType>(AlertType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Multi-Factor Authentication Types

  public getMfaTypes(): Observable<MfaType[]> {
    const url = Endpoints.getMfaTypes();
    return this.apiClient.getArr<AlertType>(AlertType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Member Employment History

  public getMemberEmploymentStatusTypes(): Observable<EmploymentStatusType[]> {
    const url = Endpoints.getMemberEmploymentStatusTypes();
    return this.apiClient.getArr<EmploymentStatusType>(EmploymentStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberEmploymentStatusChangeReasonTypes(): Observable<StatusReasonType[]> {
    const url = Endpoints.getMemberEmploymentStatusChangeReasonTypes();
    return this.apiClient.getArr<StatusReasonType>(StatusReasonType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getScheduledRemittanceFrequencyTypes(): Observable<RemittanceFrequency[]> {
    const url = Endpoints.getScheduledRemittanceFrequencyTypes();
    return this.apiClient.getArr<RemittanceFrequency>(RemittanceFrequency, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getScheduledRemittanceStatusTypes(): Observable<ScheduledRemittanceStatus[]> {
    const url = Endpoints.getScheduledRemittanceStatusTypes();
    return this.apiClient.getArr<ScheduledRemittanceStatus>(ScheduledRemittanceStatus, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Fund Types

  public getFundTypes(): Observable<FundType[]> {
    const url = Endpoints.getFundTypes();
    return this.apiClient.getArr<FundType>(FundType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Beneficiary Relationship to Member Types
  public getBeneficiaryRelationshipToMemberTypes(): Observable<BeneficiaryRelationshipToMemberType[]> {
    const url = Endpoints.getBeneficiaryRelationshipToMemberTypes();
    return this.apiClient.getArr<BeneficiaryRelationshipToMemberType>(BeneficiaryRelationshipToMemberType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Accounts

  public getAccountTypes(): Observable<AccountType[]> {
    const url = Endpoints.getAccountTypes();
    return this.apiClient.getArr<AccountType>(AccountType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAccountStatusTypes(): Observable<AccountStatusType[]> {
    const url = Endpoints.getAccountStatusTypes();
    return this.apiClient.getArr<AccountStatusType>(AccountStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Fiduciaries

  public getFiduciaryTypes(): Observable<FiduciaryType[]> {
    const url = Endpoints.getFiduciaryTypes();
    return this.apiClient.getArr<FiduciaryType>(FiduciaryType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getFiduciaryEntityTypes(): Observable<FiduciaryEntityType[]> {
    const url = Endpoints.getFiduciaryEntityTypes();
    return this.apiClient.getArr<FiduciaryEntityType>(FiduciaryEntityType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getFiduciaryPowerOfAttorneyTypes(): Observable<FiduciaryPowerOfAttorneyType[]> {
    const url = Endpoints.getFiduciaryPowerOfAttorneyTypes();
    return this.apiClient.getArr<FiduciaryPowerOfAttorneyType>(FiduciaryPowerOfAttorneyType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getFiduciaryPowerOfAttorneyAppointmentTypes(): Observable<FiduciaryPowerOfAttorneyAppointmentType[]> {
    const url = Endpoints.getFiduciaryPowerOfAttorneyAppointmentTypes();
    return this.apiClient
      .getArr<FiduciaryPowerOfAttorneyAppointmentType>(FiduciaryPowerOfAttorneyAppointmentType, url)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getEmployerStatusTypes(): Observable<EmployerStatusType[]> {
    const url = Endpoints.employerStatusTypes();
    return this.apiClient.getArr<EmployerStatusType>(EmployerStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberStatusTypes(): Observable<MemberStatusType[]> {
    const url = Endpoints.memberStatusTypes();
    return this.apiClient.getArr<MemberStatusType>(MemberStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getRetirementTypes(): Observable<RetirementType[]> {
    const url = Endpoints.getRetirementTypes();
    return this.apiClient.getArr<RetirementType>(RetirementType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getLifeStatusTypes(): Observable<LifeStatusType[]> {
    const url = Endpoints.getLifeStatusTypes();
    return this.apiClient.getArr<LifeStatusType>(LifeStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getDeathRecordContactMethodTypes(): Observable<BaseType[]> {
    const url = Endpoints.getDeathRecordContactMethodTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
  public getDeathRecordSettlementTypes(): Observable<BaseType[]> {
    const url = Endpoints.getDeathRecordSettlementTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTransferInSourceAccountTypes(): Observable<BaseType[]> {
    const url = Endpoints.getTransferInSourceAccountTypes();
    return this.apiClient.getArr<BaseType>(BaseType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getConsentWaiverTypes(): Observable<ConsentWaiverType[]> {
    const url = Endpoints.ipGetConsentWaiverTypes();
    return this.apiClient.getArr<ConsentWaiverType>(ConsentWaiverType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // Taxes

  public getTaxFormTypes(): Observable<TaxFormType[]> {
    const url = Endpoints.getTaxFormTypes();
    return this.apiClient.getArr<TaxFormType>(TaxFormType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxResidencyTypes(): Observable<TaxResidencyType[]> {
    const url = Endpoints.getTaxResidencyTypes();
    return this.apiClient.getArr<TaxResidencyType>(TaxResidencyType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxStatusTypes(): Observable<TaxStatusType[]> {
    const url = Endpoints.getTaxStatusTypes();
    return this.apiClient.getArr<TaxStatusType>(TaxStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxProfileSourceTypes(): Observable<TaxProfileSourceType[]> {
    const url = Endpoints.getTaxProfileSourceTypes();
    return this.apiClient.getArr<TaxProfileSourceType>(TaxProfileSourceType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getPrescribedTaxZoneTypes(): Observable<PrescribedTaxZoneType[]> {
    const url = Endpoints.getPrescribedTaxZoneTypes();
    return this.apiClient.getArr<PrescribedTaxZoneType>(PrescribedTaxZoneType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxWithholdingOverrideTypes(): Observable<TaxWithholdingOverrideType[]> {
    const url = Endpoints.getTaxWithholdingOverrideTypes();
    return this.apiClient.getArr<TaxWithholdingOverrideType>(TaxWithholdingOverrideType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getTaxCraLetterOfAuthorityTypes(): Observable<TaxCraLetterOfAuthorityType[]> {
    const url = Endpoints.getTaxCraLetterOfAuthorityTypes();
    return this.apiClient.getArr<TaxCraLetterOfAuthorityType>(TaxCraLetterOfAuthorityType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getPaymentMethodTypes(): Observable<PaymentMethodType[]> {
    const url = Endpoints.getPaymentMethodTypes();
    return this.apiClient.getArr<PaymentMethodType>(PaymentMethodType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getFinancialInstitutionTypes(): Observable<FinancialInstitutionType[]> {
    const url = Endpoints.getFinancialInstitutionTypes();
    return this.apiClient.getArr<FinancialInstitutionType>(FinancialInstitutionType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getMemberContributionFrequencyTypes(): Observable<MemberContributionFrequencyType[]> {
    const url = Endpoints.getMemberContributionFrequencyTypes();
    return this.apiClient.getArr<MemberContributionFrequencyType>(MemberContributionFrequencyType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getCreditCardStatusTypes(): Observable<CreditCardStatusType[]> {
    const url = Endpoints.getCreditCardStatusTypes();
    return this.apiClient.getArr<CreditCardStatusType>(CreditCardStatusType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
